import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(localStorage.getItem("activeWorkspaceId"));

  const applySession = (data) => {
    setUser(data.user);
    setWorkspaces(data.workspaces);
    const stored = localStorage.getItem("activeWorkspaceId");
    const validStored = data.workspaces.find((w) => w.id === stored);
    const nextId = validStored ? stored : data.workspaces[0]?.id;
    if (nextId) {
      setActiveWorkspaceId(nextId);
      localStorage.setItem("activeWorkspaceId", nextId);
    }
  };

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      applySession(data);
    } catch (e) {
      setUser(null);
      setWorkspaces([]);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password, invite_token) => {
    try {
      const { data } = await api.post("/auth/login", { email, password, invite_token });
      applySession(data);
    } catch (err) {
      throw new Error(formatApiError(err.response?.data?.detail) || "Login failed");
    }
  };

  const register = async (name, email, password, invite_token, accepted_terms = false, organization = "") => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password, invite_token, accepted_terms, organization });
      applySession(data);
    } catch (err) {
      throw new Error(formatApiError(err.response?.data?.detail) || "Registration failed");
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
    setWorkspaces([]);
    localStorage.removeItem("activeWorkspaceId");
  };

  const switchWorkspace = (id) => {
    setActiveWorkspaceId(id);
    localStorage.setItem("activeWorkspaceId", id);
  };

  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId) || workspaces[0];

  return (
    <AuthContext.Provider value={{ user, workspaces, activeWorkspace, switchWorkspace, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
